import { useEffect, useState, useSyncExternalStore } from 'react';
import type { FrameSource, VisualFrame } from '@erp/ui/lib/audio/types';
import {
  VoiceCallSession,
  type VoiceCallHandlers,
  type VoiceCallState,
} from './voiceCallSession';

export type { VoiceCallFailure, VoiceCallStatus } from './voiceCallSession';

export interface UseVoiceCallOptions extends VoiceCallHandlers {
  /** False hangs up, e.g. when the panel holding the call controls closes. */
  active?: boolean;
}

export interface VoiceCallControls extends VoiceCallState {
  /** Visual frames of the caller's mic and of the assistant's voice. */
  micVisual: FrameSource<VisualFrame>;
  assistantVisual: FrameSource<VisualFrame>;
  toggleMute: () => void;
  start: () => Promise<void>;
  hangUp: () => void;
  /**
   * Tell the voice that the user shared their screen or a file with the
   * assistant, so it delegates questions about it instead of saying it can't
   * see it.
   */
  noteShared: (kind: 'screen' | 'file') => void;
}

/**
 * A GPT-Live voice call in front of the chat agent — see
 * {@link VoiceCallSession}. The call lives outside React; this hook owns one
 * for the component's lifetime, hangs it up on unmount or when deactivated
 * (an orphaned call keeps billing), and renders its state.
 */
export function useVoiceCall({
  onDelegate,
  onVoiceTurn,
  onError,
  active = true,
}: UseVoiceCallOptions): VoiceCallControls {
  const [session] = useState(() => new VoiceCallSession());
  useEffect(() => {
    session.setHandlers({ onDelegate, onVoiceTurn, onError });
  });
  useEffect(() => session.hangUp, [session]);
  useEffect(() => {
    if (!active) session.hangUp();
  }, [active, session]);
  const state = useSyncExternalStore(session.subscribe, session.getSnapshot);

  return {
    ...state,
    micVisual: session.micVisual,
    assistantVisual: session.assistantVisual,
    toggleMute: session.toggleMute,
    start: session.start,
    hangUp: session.hangUp,
    noteShared: session.noteShared,
  };
}
